import { FC, useState } from "react";
import { Badge, Button, Card, Spinner, Table } from "flowbite-react";
import { HiOfficeBuilding, HiChevronLeft, HiChevronRight } from "react-icons/hi";
import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import NavbarSidebarLayout from "@/layouts/navbar-sidebar";
import { useGetAllCompaniesQuery } from "@/api/companyApi/company";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { agentRoles } from "@/constants/agent";

const PAGE_SIZE = 15;

const CompanyList: FC = () => {
  const { t } = useTranslation(["company", "common"]);
  const user = useCurrentUser();
  const isSuperAdmin = user?.agent_role === agentRoles.super_admin;
  const [page, setPage] = useState(1);

  const { data, isLoading, isFetching } = useGetAllCompaniesQuery(
    { page, pageSize: PAGE_SIZE },
    { skip: !isSuperAdmin, refetchOnMountOrArgChange: true }
  );

  if (!isSuperAdmin) {
    return <Navigate to="/" replace />;
  }

  const companies = data?.items ?? [];
  const totalPages = data?.pagination?.totalPages ?? 1;
  const totalItems = data?.pagination?.totalItems ?? companies.length;

  return (
    <NavbarSidebarLayout>
      <div className="px-4 pt-6">
        <div className="mb-6 flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-violet-500 text-white shadow-md shadow-indigo-500/30">
            <HiOfficeBuilding className="h-6 w-6" />
          </span>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">
              {t("company:companiesTitle")}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t("company:companiesSubtitle", { count: totalItems })}
            </p>
          </div>
        </div>

        <Card>
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Spinner size="xl" />
            </div>
          ) : companies.length === 0 ? (
            <p className="py-8 text-center text-gray-500 dark:text-gray-400">
              {t("company:noCompanies")}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table hoverable>
                <Table.Head>
                  <Table.HeadCell>{t("company:table.name")}</Table.HeadCell>
                  <Table.HeadCell>{t("company:table.domain")}</Table.HeadCell>
                  <Table.HeadCell>{t("company:table.website")}</Table.HeadCell>
                  <Table.HeadCell>{t("company:table.country")}</Table.HeadCell>
                  <Table.HeadCell>{t("company:table.sector")}</Table.HeadCell>
                  <Table.HeadCell>{t("company:table.status")}</Table.HeadCell>
                </Table.Head>
                <Table.Body className="divide-y">
                  {companies.map((company) => (
                    <Table.Row
                      key={company.company_id}
                      className="bg-white dark:border-gray-700 dark:bg-gray-800"
                    >
                      <Table.Cell className="whitespace-nowrap font-medium text-gray-900 dark:text-white">
                        {company.company_name}
                      </Table.Cell>
                      <Table.Cell>{company.company_domain || "—"}</Table.Cell>
                      <Table.Cell>{company.company_website || "—"}</Table.Cell>
                      <Table.Cell>{company.country || "—"}</Table.Cell>
                      <Table.Cell>{company.sector_type || "—"}</Table.Cell>
                      <Table.Cell>
                        <Badge
                          color={company.is_active === false ? "gray" : "success"}
                          className="w-fit"
                        >
                          {company.is_active === false
                            ? t("company:status.inactive")
                            : t("company:status.active")}
                        </Badge>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          )}

          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {t("company:pageOf", { page, totalPages })}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  color="light"
                  disabled={page <= 1 || isFetching}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <HiChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  color="light"
                  disabled={page >= totalPages || isFetching}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  <HiChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </NavbarSidebarLayout>
  );
};

export default CompanyList;
